import React, { useCallback, useEffect, useState } from 'react';
import { HiPencil, HiPlus, HiTrash } from 'react-icons/hi';
import apiClient from '../api/client';
import { useNotification } from '../context/NotificationContext';
import { useWbNavigate } from './WbRouteContext';
import WbShell, { WbTopBar } from './WbShell';

function ClientChecklist({ clients, selectedIds, onChange, disabled }) {
  if (!clients.length) {
    return <p className="text-sm text-[var(--wb-text-muted)]">Нет доступных кабинетов</p>;
  }
  return (
    <div className="max-h-56 overflow-y-auto border border-[var(--wb-border)] rounded-lg p-2 space-y-1">
      {clients.map((c) => {
        const checked = selectedIds.includes(c.id);
        return (
          <label
            key={c.id}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-[var(--wb-surface-muted)] ${
              disabled ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={() => {
                if (disabled) return;
                onChange(
                  checked ? selectedIds.filter((id) => id !== c.id) : [...selectedIds, c.id]
                );
              }}
              className="rounded border-[var(--wb-border-strong)]"
            />
            <span className="text-sm truncate">{c.name}</span>
            <span className="text-xs text-[var(--wb-text-muted)] ml-auto shrink-0">#{c.id}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function WbAdminAccess() {
  const navigate = useWbNavigate();
  const { confirm } = useNotification();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [staff, setStaff] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [demos, setDemos] = useState([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [grantedCreds, setGrantedCreds] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createClientIds, setCreateClientIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const [showDemoForm, setShowDemoForm] = useState(false);
  const [demoEmail, setDemoEmail] = useState('');
  const [demoPassword, setDemoPassword] = useState('');
  const [demoDays, setDemoDays] = useState(7);
  const [demoMaxCampaigns, setDemoMaxCampaigns] = useState(10);
  const [demoMaxNms, setDemoMaxNms] = useState(50);
  const [demoNote, setDemoNote] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editClientIds, setEditClientIds] = useState([]);
  const [passwordEditId, setPasswordEditId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const notify = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const [staffRes, clientsRes, demosRes] = await Promise.all([
        apiClient.get('/admin/staff'),
        apiClient.get('/clients'),
        apiClient.get('/admin/demo-access').catch(() => ({ data: [] })),
      ]);
      setStaff(Array.isArray(staffRes.data) ? staffRes.data : []);
      setAllClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      setDemos(Array.isArray(demosRes.data) ? demosRes.data : []);
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
        return;
      }
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }
      notify(err.response?.data?.message || 'Не удалось загрузить данные', 'error');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login');
      return;
    }
    loadData();
  }, [loadData, navigate]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createEmail.trim() || !createPassword) {
      notify('Укажите email и пароль', 'error');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/admin/staff', {
        email: createEmail.trim(),
        password: createPassword,
        clientIds: createClientIds,
      });
      notify('Пользователь создан', 'success');
      setShowCreate(false);
      setCreateEmail('');
      setCreatePassword('');
      setCreateClientIds([]);
      await loadData();
    } catch (err) {
      notify(err.response?.data?.message || 'Ошибка создания', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleGrantDemo = async (e) => {
    e.preventDefault();
    if (!demoEmail.trim()) {
      notify('Укажите email', 'error');
      return;
    }
    setSaving(true);
    setGrantedCreds(null);
    try {
      const { data } = await apiClient.post('/admin/demo-access', {
        email: demoEmail.trim(),
        password: demoPassword.trim() || undefined,
        days: Number(demoDays) || 7,
        max_campaigns: Number(demoMaxCampaigns) || 10,
        max_nms: Number(demoMaxNms) || 50,
        note: demoNote.trim() || undefined,
        seed: true,
      });
      setGrantedCreds({
        email: data?.user?.email,
        password: data?.password,
        ends_at: data?.user?.demo_ends_at,
        client_id: data?.client_id,
      });
      notify(data?.message || 'Демо-доступ выдан', 'success');
      setShowDemoForm(false);
      setDemoEmail('');
      setDemoPassword('');
      setDemoNote('');
      await loadData();
    } catch (err) {
      notify(err.response?.data?.message || 'Ошибка выдачи демо', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleExtendDemo = async (userId) => {
    setSaving(true);
    try {
      await apiClient.patch(`/admin/demo-access/${userId}`, { days: 7, extend_from_now: true });
      notify('Демо продлено на 7 дней', 'success');
      await loadData();
    } catch (err) {
      notify(err.response?.data?.message || 'Ошибка продления', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeDemo = async (userId, email) => {
    const ok = await confirm({
      message: `Снять демо-режим у ${email}? Аккаунт останется (full), кабинет не удалится.`,
      confirmText: 'Снять демо',
    });
    if (!ok) return;
    setSaving(true);
    try {
      await apiClient.delete(`/admin/demo-access/${userId}`);
      notify('Демо снято', 'success');
      await loadData();
    } catch (err) {
      notify(err.response?.data?.message || 'Ошибка', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEditClients = (member) => {
    setEditingId(member.id);
    setEditClientIds((member.clients || []).map((c) => c.id));
    setPasswordEditId(null);
  };

  const saveClients = async (userId) => {
    setSaving(true);
    try {
      await apiClient.put(`/admin/staff/${userId}/clients`, { clientIds: editClientIds });
      notify('Доступ к кабинетам обновлён', 'success');
      setEditingId(null);
      await loadData();
    } catch (err) {
      notify(err.response?.data?.message || 'Ошибка сохранения', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await apiClient.patch(`/admin/staff/${id}/toggle`);
      await loadData();
    } catch (err) {
      notify(err.response?.data?.message || 'Ошибка', 'error');
    }
  };

  const handleDelete = async (id, email) => {
    const ok = await confirm({
      message: `Удалить доступ для ${email}?`,
      danger: true,
      confirmText: 'Удалить',
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/admin/staff/${id}`);
      notify('Пользователь удалён', 'success');
      await loadData();
    } catch (err) {
      notify(err.response?.data?.message || 'Ошибка удаления', 'error');
    }
  };

  const handlePasswordSave = async (id) => {
    if (!newPassword || newPassword.length < 6) {
      notify('Пароль не короче 6 символов', 'error');
      return;
    }
    setSaving(true);
    try {
      await apiClient.patch(`/admin/staff/${id}/password`, { password: newPassword });
      notify('Пароль обновлён', 'success');
      setPasswordEditId(null);
      setNewPassword('');
    } catch (err) {
      notify(err.response?.data?.message || 'Ошибка смены пароля', 'error');
    } finally {
      setSaving(false);
    }
  };

  const msgClass =
    messageType === 'error'
      ? 'text-[var(--wb-danger)] bg-[var(--wb-danger-bg)]'
      : messageType === 'success'
      ? 'text-[var(--wb-success)] bg-[var(--wb-success-bg)]'
      : 'text-[var(--wb-primary)] bg-[var(--wb-primary-soft)]';

  if (forbidden) {
    return (
      <WbShell>
        <WbTopBar title="Доступы" subtitle="Только для администратора" backTo={() => navigate('/dashboard')} />
        <main className="max-w-2xl mx-auto p-6">
          <div className="wb-card p-8 text-center text-[var(--wb-danger)]">
            Раздел доступен только администратору
          </div>
        </main>
      </WbShell>
    );
  }

  return (
    <WbShell>
      <WbTopBar
        title="Доступы по email"
        subtitle="Staff-доступ к кабинетам и демо-период для клиентов"
        backTo={() => navigate('/dashboard')}
      />

      <main className="max-w-3xl mx-auto p-6 space-y-6">
        {message && (
          <p className={`text-sm rounded-lg px-4 py-2.5 ${msgClass}`} role="status">
            {message}
          </p>
        )}

        {grantedCreds && (
          <div className="wb-card p-4 border border-amber-200 bg-amber-50 space-y-1 text-sm">
            <p className="font-semibold text-amber-950">Сохраните данные для клиента</p>
            <p>
              Логин: <code className="font-mono">{grantedCreds.email}</code>
            </p>
            <p>
              Пароль: <code className="font-mono">{grantedCreds.password}</code>
            </p>
            {grantedCreds.ends_at && (
              <p className="text-amber-900/80">
                Демо до {new Date(grantedCreds.ends_at).toLocaleString('ru-RU')}
              </p>
            )}
            <button
              type="button"
              className="wb-btn-secondary text-xs mt-2"
              onClick={() => setGrantedCreds(null)}
            >
              Скрыть
            </button>
          </div>
        )}

        {/* ─── Демо ─── */}
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-semibold text-base">Демо-доступ</h2>
              <p className="text-sm text-[var(--wb-text-secondary)] mt-0.5">
                Обычный кабинет через WB API: клиент вводит свой ключ, лимиты — создание кампаний и артикулы воронки.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowDemoForm((v) => !v)}
              className="wb-btn shrink-0"
            >
              <HiPlus className="w-4 h-4" />
              Выдать демо
            </button>
          </div>

          {showDemoForm && (
            <form onSubmit={handleGrantDemo} className="wb-card p-6 space-y-4">
              <div>
                <label htmlFor="demo-email" className="block text-sm font-medium mb-1.5">
                  Email клиента
                </label>
                <input
                  id="demo-email"
                  type="email"
                  value={demoEmail}
                  onChange={(e) => setDemoEmail(e.target.value)}
                  className="wb-input"
                  placeholder="client@company.ru"
                  required
                  autoComplete="off"
                />
              </div>
              <div>
                <label htmlFor="demo-password" className="block text-sm font-medium mb-1.5">
                  Пароль (необязательно — сгенерируем)
                </label>
                <input
                  id="demo-password"
                  type="text"
                  value={demoPassword}
                  onChange={(e) => setDemoPassword(e.target.value)}
                  className="wb-input"
                  placeholder="оставьте пустым"
                  autoComplete="off"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label htmlFor="demo-days" className="block text-sm font-medium mb-1.5">
                    Дней
                  </label>
                  <input
                    id="demo-days"
                    type="number"
                    min={1}
                    max={90}
                    value={demoDays}
                    onChange={(e) => setDemoDays(e.target.value)}
                    className="wb-input"
                  />
                </div>
                <div>
                  <label htmlFor="demo-camps" className="block text-sm font-medium mb-1.5">
                    Можно создать кампаний
                  </label>
                  <input
                    id="demo-camps"
                    type="number"
                    min={0}
                    max={20}
                    value={demoMaxCampaigns}
                    onChange={(e) => setDemoMaxCampaigns(e.target.value)}
                    className="wb-input"
                  />
                </div>
                <div>
                  <label htmlFor="demo-nms" className="block text-sm font-medium mb-1.5">
                    Артикулов в воронке
                  </label>
                  <input
                    id="demo-nms"
                    type="number"
                    min={1}
                    max={200}
                    value={demoMaxNms}
                    onChange={(e) => setDemoMaxNms(e.target.value)}
                    className="wb-input"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="demo-note" className="block text-sm font-medium mb-1.5">
                  Заметка
                </label>
                <input
                  id="demo-note"
                  type="text"
                  value={demoNote}
                  onChange={(e) => setDemoNote(e.target.value)}
                  className="wb-input"
                  placeholder="лид с вебинара / менеджер…"
                />
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={saving} className="wb-btn">
                  {saving ? 'Выдача...' : 'Выдать демо'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDemoForm(false)}
                  className="wb-btn-secondary"
                >
                  Отмена
                </button>
              </div>
            </form>
          )}

          {!loading && demos.length === 0 && (
            <div className="wb-card p-6 text-center text-sm text-[var(--wb-text-muted)]">
              Демо-пользователей пока нет
            </div>
          )}
          {!loading && demos.length > 0 && (
            <ul className="space-y-3">
              {demos.map((d) => {
                const expired = Boolean(d.demo_expired) || (d.demo_ends_at && new Date(d.demo_ends_at) < new Date());
                return (
                  <li key={d.id} className="wb-card p-4 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{d.email}</p>
                      <p className="text-xs text-[var(--wb-text-muted)] mt-1 space-x-2">
                        <span className={expired ? 'text-[var(--wb-danger)]' : 'text-[var(--wb-success)]'}>
                          {expired ? 'Истёк' : 'Активен'}
                        </span>
                        {d.demo_ends_at && (
                          <span>
                            до {new Date(d.demo_ends_at).toLocaleDateString('ru-RU')}
                          </span>
                        )}
                        <span>
                          ≤{d.demo_max_campaigns} камп. / ≤{d.demo_max_nms} арт.
                        </span>
                        {d.client_name && <span>· {d.client_name}</span>}
                      </p>
                      {d.demo_note && (
                        <p className="text-xs text-[var(--wb-text-secondary)] mt-1">{d.demo_note}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleExtendDemo(d.id)}
                        className="wb-btn-secondary text-sm py-1.5 px-3"
                      >
                        +7 дней
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleRevokeDemo(d.id, d.email)}
                        className="wb-btn-secondary text-sm py-1.5 px-3 text-[var(--wb-danger)]"
                      >
                        Снять демо
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <hr className="border-[var(--wb-border)]" />

        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-semibold text-base">Staff-доступ</h2>
            <p className="text-sm text-[var(--wb-text-secondary)] mt-0.5">
              Пользователи входят логином/паролем и видят только выбранные кабинеты.
            </p>
          </div>
          <button type="button" onClick={() => setShowCreate((v) => !v)} className="wb-btn shrink-0">
            <HiPlus className="w-4 h-4" />
            Добавить
          </button>
        </div>

        {showCreate && (
          <form onSubmit={handleCreate} className="wb-card p-6 space-y-4">
            <h2 className="font-semibold">Новый пользователь</h2>
            <div>
              <label htmlFor="staff-email" className="block text-sm font-medium mb-1.5">
                Логин
              </label>
              <input
                id="staff-email"
                type="text"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                className="wb-input"
                placeholder="email или логин"
                required
                autoComplete="off"
              />
            </div>
            <div>
              <label htmlFor="staff-password" className="block text-sm font-medium mb-1.5">
                Пароль
              </label>
              <input
                id="staff-password"
                type="password"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                className="wb-input"
                placeholder="не менее 6 символов"
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>
            <div>
              <p className="block text-sm font-medium mb-1.5">Кабинеты WB</p>
              <ClientChecklist
                clients={allClients}
                selectedIds={createClientIds}
                onChange={setCreateClientIds}
                disabled={saving}
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={saving} className="wb-btn">
                {saving ? 'Сохранение...' : 'Создать'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)} className="wb-btn-secondary">
                Отмена
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="wb-card p-10 text-center text-[var(--wb-text-muted)]">Загрузка...</div>
        ) : staff.length === 0 ? (
          <div className="wb-card p-10 text-center text-[var(--wb-text-muted)]">
            Пользователей с доступом пока нет
          </div>
        ) : (
          <ul className="space-y-3">
            {staff.map((member) => (
              <li key={member.id} className="wb-card p-5 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{member.email}</p>
                    <p className="text-xs text-[var(--wb-text-muted)] mt-0.5">
                      {member.is_active ? (
                        <span className="text-[var(--wb-success)]">Активен</span>
                      ) : (
                        <span className="text-[var(--wb-danger)]">Заблокирован</span>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleToggle(member.id)}
                      className="wb-btn-secondary text-sm py-1.5 px-3"
                    >
                      {member.is_active ? 'Заблокировать' : 'Разблокировать'}
                    </button>
                    <button
                      type="button"
                      onClick={() => startEditClients(member)}
                      className="wb-btn-secondary text-sm py-1.5 px-3 inline-flex items-center gap-1"
                    >
                      <HiPencil className="w-4 h-4" />
                      Кабинеты
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPasswordEditId(member.id);
                        setNewPassword('');
                        setEditingId(null);
                      }}
                      className="wb-btn-secondary text-sm py-1.5 px-3"
                    >
                      Пароль
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(member.id, member.email)}
                      className="p-2 rounded-lg text-[var(--wb-danger)] hover:bg-[var(--wb-danger-bg)]"
                      aria-label="Удалить"
                    >
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {editingId === member.id ? (
                  <div className="pt-2 border-t border-[var(--wb-border)] space-y-3">
                    <ClientChecklist
                      clients={allClients}
                      selectedIds={editClientIds}
                      onChange={setEditClientIds}
                      disabled={saving}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => saveClients(member.id)}
                        className="wb-btn text-sm"
                      >
                        Сохранить
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="wb-btn-secondary text-sm"
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[var(--wb-text-secondary)]">
                    {(member.clients || []).length > 0
                      ? (member.clients || []).map((c) => c.name).join(', ')
                      : 'Кабинеты не назначены'}
                  </p>
                )}

                {passwordEditId === member.id && (
                  <div className="pt-2 border-t border-[var(--wb-border)] flex flex-wrap gap-2 items-end">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm font-medium mb-1.5">Новый пароль</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="wb-input"
                        minLength={6}
                        autoComplete="new-password"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => handlePasswordSave(member.id)}
                      className="wb-btn text-sm"
                    >
                      Сохранить
                    </button>
                    <button
                      type="button"
                      onClick={() => setPasswordEditId(null)}
                      className="wb-btn-secondary text-sm"
                    >
                      Отмена
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>
    </WbShell>
  );
}
